import React, { useState, useEffect, useMemo } from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal } from '../components/Modal';
import { CustomerDetailModal } from '../components/CustomerDetailModal';
import { useToast } from '../components/Toast';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { customersService } from '../services/firebaseService';
import { Users, Download, Eye, Search, ArrowUpDown, TrendingUp, ShoppingBag, X, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';

export const Customers = () => {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [monthlyRevenue, setMonthlyRevenue] = useState({ ca_this_month: 0, ca_last_month: 0, orders_this_month: 0 });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const CLIENTS_PER_PAGE = 50;

  // Modal historique
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyType, setHistoryType] = useState('ca'); // 'ca', 'orders', 'customers', 'avg'
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadMonthlyRevenue();
  }, []);

  const loadCustomers = async () => {
    try {
      // FIREBASE DIRECT
      const result = await customersService.getAll();
      setCustomers(result.customers || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlyRevenue = async () => {
    try {
      // FIREBASE DIRECT
      const result = await customersService.getMonthlyRevenue();
      setMonthlyRevenue(result || { ca_this_month: 0, ca_last_month: 0, orders_this_month: 0 });
    } catch (error) {
      console.error('Failed to load monthly revenue:', error);
    }
  };

  const loadHistoryData = async () => {
    setLoadingHistory(true);
    try {
      // FIREBASE DIRECT
      const result = await customersService.getStatsHistory(12);
      setHistoryData(result.monthly || []);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Erreur', 'Impossible de charger l\'historique');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistoryModal = async (type) => {
    setHistoryType(type);
    setShowHistoryModal(true);
    await loadHistoryData();
  };

  // Calcul des stats globales - MOIS CALENDAIRE EN COURS
  const globalStats = useMemo(() => {
    try {
      if (!Array.isArray(customers)) {
        return { total: 0, totalRevenue: 0, totalOrders: 0, avgPerCustomer: 0, activeCustomers: 0 };
      }

      const total = customers.length;
      const totalRevenue = customers.reduce((sum, c) => sum + (c?.total_spent || 0), 0);
      const totalOrders = customers.reduce((sum, c) => sum + (c?.order_count || c?.total_orders || 0), 0);
      const avgPerCustomer = total > 0 ? totalRevenue / total : 0;

      // Clients actifs (commande dans le mois calendaire en cours)
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const activeCustomers = customers.filter(c => {
        if (!c?.last_order_at) return false;
        try {
          const lastOrder = new Date(c.last_order_at);
          return lastOrder >= firstDayOfMonth;
        } catch {
          return false;
        }
      }).length;

      return { total, totalRevenue, totalOrders, avgPerCustomer, activeCustomers };
    } catch (error) {
      console.error('Error computing globalStats:', error);
      return { total: 0, totalRevenue: 0, totalOrders: 0, avgPerCustomer: 0, activeCustomers: 0 };
    }
  }, [customers]);

  // Filtrage et tri
  const filteredCustomers = useMemo(() => {
    let result = [...customers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (c.phone || '').includes(term)
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'created_at' || sortField === 'last_order_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (typeof aVal === 'number' || sortField === 'total_spent' || sortField === 'order_count') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [customers, searchTerm, sortField, sortDirection]);

  // Reset page quand recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination des résultats filtrés
  const totalPages = Math.ceil(filteredCustomers.length / CLIENTS_PER_PAGE);
  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * CLIENTS_PER_PAGE;
    return filteredCustomers.slice(startIndex, startIndex + CLIENTS_PER_PAGE);
  }, [filteredCustomers, currentPage, CLIENTS_PER_PAGE]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getMonthlyAverage = (customer) => {
    if (!customer.created_at || !customer.total_spent) return 0;
    const created = new Date(customer.created_at);
    const now = new Date();
    const months = Math.max(1, (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth()) + 1);
    return (customer.total_spent / months).toFixed(2);
  };

  const handleExportCSV = () => {
    if (filteredCustomers.length === 0) {
      toast.error('Export impossible', 'Aucun client à exporter');
      return;
    }

    const headers = [
      'Nom', 'Prénom', 'Email', 'Téléphone', 'Adresse',
      'Date inscription', 'Dernière commande',
      'Nb commandes', 'Total dépensé (€)', 'Moyenne/mois (€)', 'Fidélité (€)'
    ];

    const rows = filteredCustomers.map(customer => [
      customer.last_name || customer.name?.split(' ').pop() || '',
      customer.first_name || customer.name?.split(' ')[0] || '',
      customer.email || '',
      customer.phone || '',
      customer.address || '',
      formatDate(customer.created_at),
      formatDate(customer.last_order_at),
      customer.order_count || customer.total_orders || 0,
      (customer.total_spent || 0).toFixed(2),
      getMonthlyAverage(customer),
      (customer.loyalty_balance || customer.loyalty_points || 0).toFixed(2)
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `clients_familys_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Helper pour le titre du modal
  const getHistoryTitle = () => {
    switch(historyType) {
      case 'ca': return '💰 Historique CA - 12 mois';
      case 'orders': return '🛒 Historique Commandes - 12 mois';
      case 'customers': return '👥 Historique Clients Actifs - 12 mois';
      case 'avg': return '📊 Historique Moyenne/Client - 12 mois';
      default: return 'Historique';
    }
  };

  // Helper pour la valeur à afficher dans l'historique
  const getHistoryValue = (month) => {
    switch(historyType) {
      case 'ca': return `${month.ca.toFixed(0)}€`;
      case 'orders': return month.orders;
      case 'customers': return month.unique_customers;
      case 'avg': return `${month.avg_per_customer.toFixed(0)}€`;
      default: return '-';
    }
  };

  if (loading) return <div><Header title="Clients" /><div className="p-8">Chargement...</div></div>;

  return (
    <div>
      <Header title="Clients & Fidélité" />
      <div className="p-8">

        {/* Stats globales - CLIQUABLES */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {/* Clients total - Non cliquable */}
          <Card className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-3xl font-black text-gray-800">{globalStats.total}</p>
            <p className="text-sm text-gray-500">Clients total</p>
          </Card>

          {/* Clients actifs ce mois - Cliquable */}
          <Card
            className="p-4 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-green-300"
            onClick={() => openHistoryModal('customers')}
          >
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-3xl font-black text-gray-800">{globalStats.activeCustomers}</p>
            <p className="text-sm text-gray-500">Actifs ce mois</p>
            <p className="text-xs text-green-500 mt-1 flex items-center justify-center gap-1">
              <BarChart3 className="w-3 h-3" /> Voir historique
            </p>
          </Card>

          {/* Commandes du mois - Cliquable */}
          <Card
            className="p-4 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-purple-300"
            onClick={() => openHistoryModal('orders')}
          >
            <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-3xl font-black text-gray-800">{monthlyRevenue.orders_this_month || 0}</p>
            <p className="text-sm text-gray-500">Commandes du mois</p>
            <p className="text-xs text-purple-500 mt-1 flex items-center justify-center gap-1">
              <BarChart3 className="w-3 h-3" /> Voir historique
            </p>
          </Card>

          {/* CA du Mois - Cliquable */}
          <Card
            className="p-4 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-primary"
            onClick={() => openHistoryModal('ca')}
          >
            <span className="text-3xl">💰</span>
            <p className="text-3xl font-black text-primary">{(monthlyRevenue.ca_this_month || 0).toFixed(0)}€</p>
            <p className="text-sm text-gray-500">CA du Mois</p>
            <p className="text-xs text-gray-400 mt-1">
              {monthlyRevenue.last_month_name}: {(monthlyRevenue.ca_last_month || 0).toFixed(0)}€
            </p>
            <p className="text-xs text-primary mt-1 flex items-center justify-center gap-1">
              <BarChart3 className="w-3 h-3" /> Voir historique
            </p>
          </Card>

          {/* Moyenne par client - Cliquable */}
          <Card
            className="p-4 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-amber-300"
            onClick={() => openHistoryModal('avg')}
          >
            <span className="text-3xl">📊</span>
            <p className="text-3xl font-black text-gray-800">
              {monthlyRevenue.orders_this_month > 0
                ? (monthlyRevenue.ca_this_month / monthlyRevenue.orders_this_month).toFixed(0)
                : 0}€
            </p>
            <p className="text-sm text-gray-500">Moy/commande</p>
            <p className="text-xs text-amber-500 mt-1 flex items-center justify-center gap-1">
              <BarChart3 className="w-3 h-3" /> Voir historique
            </p>
          </Card>
        </div>

        {/* Barre de recherche et export */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV ({filteredCustomers.length})
          </Button>
        </div>

        {filteredCustomers.length === 0 ? (
          <Card className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'Aucun client trouvé' : 'Aucun client enregistré'}
            </p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Client
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Inscrit le
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('last_order_at')}
                    >
                      <div className="flex items-center gap-1">
                        Dernière cmd
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('order_count')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Cmd
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('total_spent')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Moy/mois
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Fidélité
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-red-400 flex items-center justify-center text-white font-bold">
                            {customer.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{customer.name || 'Client'}</p>
                            <p className="text-xs text-gray-500">{customer.email}</p>
                            {customer.phone && <p className="text-xs text-gray-400">{customer.phone}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(customer.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(customer.last_order_at)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                          {customer.order_count || customer.total_orders || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">
                        {(customer.total_spent || 0).toFixed(2)}€
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {getMonthlyAverage(customer)}€
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                          💰 {(customer.loyalty_balance || customer.loyalty_points || 0).toFixed(2)}€
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowDetailModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <div className="text-sm text-gray-600">
                  Page {currentPage} sur {totalPages} ({filteredCustomers.length} clients)
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </Button>

                  {/* Numéros de pages */}
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-primary text-white'
                              : 'bg-white border hover:bg-gray-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Modal Détail Client */}
      <CustomerDetailModal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        customer={selectedCustomer}
        onRefresh={loadCustomers}
      />

      {/* Modal Historique 12 mois */}
      <Modal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        title={getHistoryTitle()}
        size="md"
      >
        <div className="p-4">
          {loadingHistory ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="text-gray-500 mt-2">Chargement...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Tableau historique */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Mois</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Valeur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((month, index) => (
                      <tr
                        key={month.month_key}
                        className={`border-b border-gray-100 ${month.is_current ? 'bg-primary/5' : ''}`}
                      >
                        <td className="px-4 py-3 text-sm">
                          <span className={month.is_current ? 'font-semibold text-primary' : 'text-gray-700'}>
                            {month.month_label}
                          </span>
                          {month.is_current && (
                            <span className="ml-2 text-xs bg-primary text-white px-2 py-0.5 rounded-full">
                              En cours
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${month.is_current ? 'text-primary' : 'text-gray-800'}`}>
                            {getHistoryValue(month)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Graphique simple en barres */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-600 mb-3">Évolution</p>
                <div className="flex items-end gap-1 h-32">
                  {[...historyData].reverse().map((month) => {
                    // Calculer la hauteur de la barre
                    let value = 0;
                    let maxValue = 1;
                    switch(historyType) {
                      case 'ca':
                        value = month.ca;
                        maxValue = Math.max(...historyData.map(m => m.ca)) || 1;
                        break;
                      case 'orders':
                        value = month.orders;
                        maxValue = Math.max(...historyData.map(m => m.orders)) || 1;
                        break;
                      case 'customers':
                        value = month.unique_customers;
                        maxValue = Math.max(...historyData.map(m => m.unique_customers)) || 1;
                        break;
                      case 'avg':
                        value = month.avg_per_customer;
                        maxValue = Math.max(...historyData.map(m => m.avg_per_customer)) || 1;
                        break;
                    }
                    const heightPercent = (value / maxValue) * 100;

                    return (
                      <div
                        key={month.month_key}
                        className="flex-1 flex flex-col items-center"
                      >
                        <div
                          className={`w-full rounded-t transition-all ${month.is_current ? 'bg-primary' : 'bg-gray-300'}`}
                          style={{ height: `${Math.max(heightPercent, 2)}%` }}
                          title={`${month.month_short}: ${getHistoryValue(month)}`}
                        />
                        <span className="text-[10px] text-gray-500 mt-1 truncate w-full text-center">
                          {month.month_short}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t flex justify-end">
                <Button variant="outline" onClick={() => setShowHistoryModal(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
